// App exits (Console D, views/health.js `#/health/exits`; APS-417, D113 phase 1): why the app
// stops. One app's latest release against the one before: all exits, crashes, force quits and
// memory-limit exits per 1,000 sessions, exits per day over 14 days with the release's shipping
// marked, and where each reason comes from on iOS and Android. The paths and screens before a
// force quit (phase 3) and example sessions by exit reason (the session search has no such filter)
// are not shown.
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { exitsDailyQuery } from '../api/queries'
import type { ExitReason } from '../api/types'
import { EmptyNote, PageHead, Section, Strip, type StripItem, Table } from '../components/d'
import { LineChart } from '../components/LineChart'
import { appLabel } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { allExits, EXIT_SOURCES, exitLabel, exitRows, per1kOf } from '../lib/exits'
import { compact } from '../lib/format'
import { Change, useHealthScope, WINDOW_DAYS } from './Health'

/** The days the chart reads, as /exits/daily serves them by default. */
const DAILY_DAYS = 14

export function HealthExitsPage() {
  const i18n = useI18n()
  const { t, number, date, rich } = i18n
  const one = (v: number) => number(v, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  const {
    orgId,
    projectId,
    now,
    build,
    week,
    appId,
    app,
    android,
    latest,
    previous,
    exits,
    exitsOf,
    hasExits,
  } = useHealthScope()
  const daily = useQuery({
    ...exitsDailyQuery(projectId, appId ?? '', new Date(now).toISOString(), build.build),
    enabled: !!appId,
  })
  const keep = { ...(appId ? { app: appId } : {}), ...build }
  const crumb = (
    <>
      <Link to="/o/$orgId/p/$projectId/health" params={{ orgId, projectId }} search={keep}>
        {t('health.title')}
      </Link>
      {' / '}
      {t('exits.title')}
    </>
  )

  if (week.isPending || (appId && exits.isPending))
    return (
      <main id="main" className="pg health">
        <Loading />
      </main>
    )
  const failed = week.isError ? week : exits.isError ? exits : null
  if (failed)
    return (
      <main id="main" className="pg health">
        <ErrorState error={failed.error} retry={() => failed.refetch()} />
      </main>
    )

  const cur = exitsOf(latest?.release)
  const before = previous ? exitsOf(previous.release) : undefined
  const rows = exitRows(cur, before)
  const quitSessions = cur?.reasons.find((x) => x.reason === 'user_quit')?.sessions ?? 0
  const of = (reason: ExitReason) =>
    [per1kOf(cur, reason), before ? per1kOf(before, reason) : null] as const
  const [crash, crashBefore] = of('crash')
  const [quit, quitBefore] = of('user_quit')
  const [memory, memoryBefore] = of('memory')
  const all = allExits(cur)
  const allBefore = before ? allExits(before) : null
  const figure = (dt: string, v: number, d: React.ReactNode): StripItem => ({
    dt,
    dd: one(v),
    small: t('health.per1k'),
    d,
  })
  // Rises in all exits and crashes read red, the rest amber, as the mock colours them.
  const strip: StripItem[] = [
    figure(
      t('exits.strip.all'),
      all,
      previous && allBefore !== null
        ? rich('exits.vsBefore', {
            change: <Change value={all - allBefore} worse="c-crash" />,
            release: previous.release,
            before: one(allBefore),
          })
        : undefined,
    ),
    figure(
      t('exits.strip.crash'),
      crash,
      previous && crashBefore !== null
        ? rich('exits.vs', {
            change: <Change value={crash - crashBefore} worse="c-crash" />,
            release: previous.release,
          })
        : undefined,
    ),
    figure(
      t('exits.strip.quit'),
      quit,
      quitBefore !== null
        ? rich('exits.quitsVs', { change: <Change value={quit - quitBefore} />, n: quitSessions })
        : t('exits.quits', { n: quitSessions }),
    ),
    figure(
      t('exits.strip.memory'),
      memory,
      previous && memoryBefore !== null
        ? rich(android ? 'exits.vs' : 'exits.vsJetsam', {
            change: <Change value={memory - memoryBefore} />,
            release: previous.release,
          })
        : undefined,
    ),
  ]
  const days = daily.data ?? []
  const shipped = latest
    ? days.findIndex((d) => d.day.slice(0, 10) === latest.released_at.slice(0, 10))
    : -1
  const perDay = (reason: ExitReason) =>
    days.map((d) =>
      d.sessions ? (d.reasons.find((x) => x.reason === reason)?.per_1k_sessions ?? 0) : null,
    )
  const label = (day: string) =>
    date(`${day.slice(0, 10)}T00:00:00Z`, { month: 'short', day: 'numeric', timeZone: 'UTC' })
  const notReported = <span className="faint">{t('exits.notReported')}</span>
  const source = (id: Parameters<typeof rich>[0] | undefined) => (id ? rich(id) : notReported)

  return (
    <main id="main" className="pg health">
      <PageHead
        crumb={crumb}
        title={t('exits.title')}
        meta={[
          app ? appLabel(app) : undefined,
          latest && cur
            ? t('exits.meta', {
                release: latest.release,
                days: WINDOW_DAYS,
                n: quitSessions,
                quits: compact(i18n, quitSessions),
                sessions: compact(i18n, cur.sessions),
              })
            : undefined,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
      {!latest || !hasExits || !cur ? (
        <EmptyNote title={t('exits.none')}>{t('exits.noneBody')}</EmptyNote>
      ) : (
        <>
          <Strip items={strip} label={t('exits.title')} translate="no" />
          <Section
            title={t('exits.daily')}
            meta={t('exits.dailyMeta', { days: days.length || DAILY_DAYS })}
          >
            {daily.isPending ? (
              <Loading />
            ) : days.every((d) => !d.sessions) ? (
              <EmptyNote title={t('exits.noDaily', { days: DAILY_DAYS })} />
            ) : (
              <LineChart
                points={days.map((d, i) => ({
                  label: label(d.day),
                  value: perDay('user_quit')[i] ?? null,
                }))}
                name={t('exits.series.quit')}
                more={(['crash', 'memory'] as const).map((reason) => {
                  const values = perDay(reason)
                  const lastValue = values.findLast((v) => v !== null)
                  const name = t(`exits.series.${reason}`)
                  return {
                    name,
                    values,
                    end: lastValue == null ? undefined : `${name} ${one(lastValue)}`,
                  }
                })}
                // The unit rides on the top tick ("150 /1k"), as on the mock's axes.
                format={(v) => `${number(v, { maximumFractionDigits: 1 })} ${t('health.per1k')}`}
                lastLabel={(p) => `${t('exits.series.quit')} ${one(p.value ?? 0)}`}
                label={t('exits.dailyLabel', { days: days.length })}
                marker={shipped >= 0 ? { index: shipped, label: latest.release } : undefined}
                markerLegend={
                  shipped >= 0 ? t('exits.shipped', { release: latest.release }) : undefined
                }
                boxLegend
                fromZero
              />
            )}
          </Section>
          <Section title={t('exits.sources')} meta={t('exits.sourcesMeta')}>
            <Table label={t('exits.sourcesLabel')}>
              <thead>
                <tr>
                  <th className="f">{t('exits.col.reason')}</th>
                  <th>{t('exits.col.ios')}</th>
                  <th>{t('exits.col.android')}</th>
                  <th className={previous ? 'num' : 'num last'} translate="no">
                    {latest.release}
                  </th>
                  {previous ? (
                    <th className="num last" translate="no">
                      {previous.release}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((x) => (
                  <tr key={x.reason}>
                    <td className="f">{t(exitLabel(x.reason, android))}</td>
                    <td className="wrap">{source(EXIT_SOURCES[x.reason]?.ios)}</td>
                    <td className="wrap">{source(EXIT_SOURCES[x.reason]?.android)}</td>
                    <td className={previous ? 'num' : 'num last'}>{one(x.v)}</td>
                    {previous ? <td className="num last">{one(x.p ?? 0)}</td> : null}
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="note hl-note">{rich('exits.sessionsNote')}</p>
            {android ? null : <p className="note hl-note">{rich('exits.userQuitNote')}</p>}
          </Section>
        </>
      )}
    </main>
  )
}
